import { isEmpty, noop } from 'lodash/fp';
import Observable from 'zen-observable';
import { combineLatest } from 'zen-observable/extras';

import { NOT_FOUND, UNPROCESSABLE_ENTITY } from '@skippay/core/constants/httpStatusCodes';
import { firstAxiosError } from '@skippay/core/utils/gqlUtils';

import AccountReferrerCreateMutation from 'mutations/AccountReferrerCreateMutation.graphql';
import MyProfileQuery from 'queries/MyProfileQuery.graphql';

import {
	clearWaitingReferrals,
	deleteWaitingReferralToken,
	getWaitingReferralTokens,
	prependWaitingReferralToken,
} from './storage';

let waitingReferralTokensObserver;
const waitingReferralTokens$ = new Observable((observer) => {
	waitingReferralTokensObserver = observer;
	return () => {
		waitingReferralTokensObserver = null;
	};
});

/**
 * Inits the referral logic, must be run only once per session.
 * @param apolloClient
 * @returns {Observable}
 */
export const init = (apolloClient) => {
	// observable of customer ID which produces as soon as user logs-in - it uses
	// `cache-only` fetch policy so it waits until the data is fetched by the login
	// logic, until then, it does not produce any value thanks to the `.filter()`.
	const customerId$ = apolloClient
		.watchQuery({ query: MyProfileQuery, fetchPolicy: 'cache-only' })
		.map(({ data }) => data?.myProfile?.id)
		.filter(Boolean);

	const subscription = combineLatest(waitingReferralTokens$, customerId$).subscribe({
		next: ([waitingReferralTokens]) => {
			if (!isEmpty(waitingReferralTokens)) {
				// we will send waiting referral tokens, one after another, to the BE
				// until the storing succeeds.
				waitingReferralTokens
					.reduce(
						(pendingPromise, waitingReferralToken) => {
							// when the previous waitingReferralToken failed to store, try the
							// next one
							return pendingPromise.catch(() =>
								apolloClient
									.mutate({
										mutation: AccountReferrerCreateMutation,
										variables: { code: waitingReferralToken },
									})
									.catch((apolloError) => {
										// when this referralToken failed, check the response status
										// code for known scenarios.
										const statusCode = firstAxiosError(apolloError)?.status;
										if (
											// Referral code not found or not applicable, e.g. its usage
											// is depleted.
											statusCode === NOT_FOUND ||
											// The customer is also referrer of the referral code, cannot
											// act as referrer and referee of one referral code.
											statusCode === UNPROCESSABLE_ENTITY
										) {
											deleteWaitingReferralToken(waitingReferralToken);
										}
										// rethrow - will try next waiting referral token
										throw apolloError;
									})
							);
						},
						// pass initially rejected promise
						Promise.reject()
					)
					.then(
						// The whole waitingReferralTokens queue is cleared out when storing
						// succeeds.
						clearWaitingReferrals,
						// When all referral tokens fails to store to the BE, the failed
						// tokens are either removed specifically from the queue (in upper
						// defined scenarios) or left in the queue for later retry in case of
						// unknown errors. The final rejection is suppressed with `noop`.
						noop
					);
			}
		},
	});
	waitingReferralTokensObserver.next(getWaitingReferralTokens());

	return subscription;
};

export const setWaitingReferralToken = (referralToken) => {
	prependWaitingReferralToken(referralToken);
	const waitingReferrals = getWaitingReferralTokens();
	waitingReferralTokensObserver.next(waitingReferrals);
};
